'use client';
import { z } from 'zod';
import { FormProvider, useForm } from 'react-hook-form';
import InputField from '../ui/react-hook-form/ui/input-field';
import { Button } from '../ui/button/button';
import { Pages } from '@/src/interfaces/pages-route';
import { useRef } from 'react';
import { CalculatorFormSchema } from './calculator-form-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useDataLayer } from '@/src/hooks/use-datalayer';
import { normalizeQuery } from '@/src/utils/normalize-search-params';
import fmt from '@/src/utils/helpers/number-format';
import { CurrencyTypeMissing, SmeQuickSearch } from '@/src/interfaces/missing-types';
import getQuickSearch from '@/src/utils/helpers/getQuickSearch';
import Heading from '../ui/heading';
import { RelLink } from '../ui/rel-link';

type Props = {
  maturities: Pages.SmeListList.ResponseBody['maturities'];
  initialAmount: string;
  initialMaturity: number;
  quickSearchActive?: boolean;
};

export const SmeBaseCalculator: React.FC<Props> = ({ maturities, initialAmount, initialMaturity, quickSearchActive = true }) => {
  const form = useForm<z.output<typeof CalculatorFormSchema>>({
    resolver: zodResolver(CalculatorFormSchema),
    mode: 'all',
    defaultValues: {
      Maturity: initialMaturity,
      Amount: fmt(Number(initialAmount), CurrencyTypeMissing.TRY, false, false, 0) as unknown as number,
    },
  });
  const searchParams = useSearchParams();
  const params = useParams();
  const normalizedSearchParams = new URLSearchParams(normalizeQuery(searchParams ?? new URLSearchParams()));

  const formRef = useRef<HTMLFormElement>(null);
  const router = useRouter();
  const { sendCustomRequestEvent } = useDataLayer();

  const quickSearch: SmeQuickSearch[] = getQuickSearch('quickSearch-smeloan') as SmeQuickSearch[] ?? [];

  const onSubmit = (data: z.output<typeof CalculatorFormSchema>) => {
    const query = new URLSearchParams();
    query.append('Amount', data.Amount as unknown as string);
    query.append('Maturity', data.Maturity as unknown as string);
    if (searchParams?.get('ChannelCampaignCode')) {
      query.append('ChannelCampaignCode', normalizedSearchParams?.get('channelcampaigncode') ?? '');
    }
    sendCustomRequestEvent(`${data.Maturity}_${data.Amount}`, 'Ticari Krediler', 'Sorgu', '');

    if (quickSearchActive) {
      let quickSearchArr = quickSearch;

      const index = quickSearchArr.findIndex(
        (arr) => arr.amount === data.Amount && arr.maturity === data.Maturity
      );

      const quickSearchItem = { amount: data.Amount, maturity: data.Maturity };

      if (index !== -1) {
        index !== 0 && quickSearchArr.reverse();
      } else if (quickSearchArr && quickSearchArr.length >= 2) {
        quickSearchArr.pop();
        quickSearchArr.unshift(quickSearchItem);
      } else if (quickSearchArr && quickSearchArr.length > 0) {
        quickSearchArr.unshift(quickSearchItem);
      } else {
        quickSearchArr.push(quickSearchItem);
      }

      localStorage.setItem('quickSearch-smeloan', JSON.stringify(quickSearchArr));
    }

    if (params?.slug && params.slug.length > 0) {
      router.push(`/ticari-kredi/sorgulama?${query}`);
    } else {
      router.push(`?${query}`);
    }
  };

  const handleQuickSearchClick = (item: SmeQuickSearch) => {
    let quickSearchArr = quickSearch;

    const index = quickSearchArr.findIndex((arr) => arr.amount === item.amount && arr.maturity === item.maturity);

    if (index !== -1) {
      index !== 0 && quickSearchArr.reverse();
    } else if (quickSearchArr && quickSearchArr.length >= 2) {
      quickSearchArr.pop();
      quickSearchArr.unshift(item);
    } else if (quickSearchArr && quickSearchArr.length > 0) {
      quickSearchArr.unshift(item);
    } else {
      quickSearchArr.push(item);
    }

    sendCustomRequestEvent(`${item.maturity}_${item.amount}`, 'Ticari Krediler', 'Sorgu', '');

    localStorage.setItem('quickSearch-smeloan', JSON.stringify(quickSearchArr));
  };

  return (
    <>
      <FormProvider {...form}>
        <form
          ref={formRef}
          className="flex flex-wrap items-end gap-6"
          onSubmit={form.handleSubmit(onSubmit)}
          data-testid="calculationForm"
        >
          <div className="relative md:w-[380px]">
            <span className="absolute right-3 top-1/2 z-10 text-base font-semibold">TL</span>
            <InputField
              disableBuiltInErrorMessage
              data-testid="amount"
              type="price"
              maxValue={9999999}
              minValue={0}
              label="Kredi Tutarı"
              name="Amount"
            />
            <div className="absolute -bottom-5 text-xs text-error md:-bottom-7 md:text-base" data-testid="amountError">
              {form.formState.errors['Amount']?.message}
            </div>
          </div>
          <InputField
            type="select"
            name="Maturity"
            label={maturities?.placeHolder ?? 'Vade'}
            showNoneOption="Vade"
            overrideClassName="md:w-[380px] w-[102px] grow md:grow-0"
            onChange={(e) => {
              const value = e.target.value;
              const item = maturities?.options?.find((item: any) => item.code === value);
              if (item) {
                form.setValue('Maturity', item?.code ? Number(item.code) : 0, { shouldValidate: true });
              }
            }}
          >
            {maturities?.options?.map(
              (maturity) =>
                maturity.code && (
                  <option key={maturity.id} value={maturity.code} data-testid="maturityOption">
                    {maturity.name} {maturities.optionValue}
                  </option>
                )
            )}
          </InputField>
          <Button
            text="Yeniden Hesapla"
            className="h-min flex-grow whitespace-nowrap rounded-lg px-8 py-4 text-base"
            type="submit"
            disabled={!form.formState.isValid}
            data-testid="calculateButton"
          />
        </form>
      </FormProvider>
      {quickSearchActive && quickSearch && quickSearch.length > 0 && (
        <section className="flex flex-col gap-2 mt-6 md:flex-row md:items-center md:gap-4">
          <Heading tagType="p" className="text-sm text-neutral900 md:text-base">
            Hızlı aramalar:
          </Heading>
          <div className="flex gap-2">
            {quickSearch.map((item) => (
              <RelLink
                key={item.amount + '|' + item.maturity}
                href={`/ticari-kredi/sorgulama?Amount=${item.amount}&Maturity=${item.maturity}`}
                className="border-b border-purple404 p-1 text-xs font-semibold text-purple404 md:text-sm"
                onClick={() => handleQuickSearchClick(item)}
              >
                {`${fmt(item?.amount, CurrencyTypeMissing.TRY, undefined, undefined, 0)}, ${item.maturity} Ay`}
              </RelLink>
            ))}
          </div>
        </section>
      )}
    </>
  );
};